import { useTracker } from '@coursera/event-pulse/react';

import type { DegreeFilterState } from 'bundles/premium-hub/components/degrees/useMultiSelectDegreeFilterStateManager';

const useDegreeFilterTracker = (degreeFilterState: DegreeFilterState) => {
  const track = useTracker();

  const {
    selectedProductVariantValue = [],
    selectedDomainValue = [],
    selectedCostValue = [],
    selectedDurationValue = [],
    selectedOpenContentAvailabilityValue = [],
  } = degreeFilterState;

  // Builds the full set of currently active `group:value` filter strings across all four
  // facets, substituting `values` for whichever facet is being committed (its state hasn't
  // re-rendered into `selected*Value` yet), so perform_search reflects every active filter.
  const buildFilterStrings = (filterName: string, values: string[]): string[] => {
    const activeValuesByFacet: Record<string, string[]> = {
      productVariant: selectedProductVariantValue,
      domain: selectedDomainValue,
      cost: selectedCostValue,
      duration: selectedDurationValue,
      openContentAvailability: selectedOpenContentAvailabilityValue,
      [filterName]: values,
    };
    return Object.entries(activeValuesByFacet).flatMap(([facet, facetValues]) =>
      facetValues.map((value) => `${facet}:${value}`)
    );
  };

  const trackPerformSearch = (filterName: string, values: string[]) => {
    // Send every currently active filter across all facets, not just this facet's values,
    // so perform_search reflects the full filter state rather than only the latest change.
    const allFilters = buildFilterStrings(filterName, values);
    if (allFilters.length) {
      track('perform_search', { filter: allFilters });
    }
  };

  return trackPerformSearch;
};

export default useDegreeFilterTracker;
